import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { PlusCircle, Warning } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import API from '../../../../../api/api'
import SelectField from '../../../../FormLegacy/FormFieldsV2/SelectField'
import { SOCIAL_LINKS_PAGE } from '../../helpers'
import Tooltip from '../Tooltip'

import ToggleInput from './ToggleInput'
import CheckboxInput from './CheckboxInput'
import TextInput from './TextInput'

const PostEventReviewSection = ({
  askForPostEventReview,
  positiveReview,
  socialLinksOptions,
  socialLinkService,
  replyTo,
  buildingId,
  setFieldValue,
}) => {
  const { t } = useTranslation(['eventManagement'])
  const hasSocialLinks = socialLinksOptions && socialLinksOptions.length > 0

  useEffect(() => {
    if (!buildingId || !positiveReview || replyTo) return

    API.getReviewContact(buildingId)
      .then((response) => {
        if (response.data?.email) {
          setFieldValue('replyTo', response.data.email)
        }
      })
      .catch(() => {})
  }, [buildingId, positiveReview])

  const handleAskForReviewChange = (name, value) => {
    setFieldValue(name, value)
    setFieldValue('positiveReview', false)
    setFieldValue('socialLinkService', '')
  }

  const handlePositiveReviewChange = (name, value) => {
    setFieldValue(name, value)
    if (!value) {
      setFieldValue('socialLinkService', '')
    }
  }

  return (
    <React.Fragment>
      <div className="columns">
        <ToggleInput
          columnSize={12}
          name="askForPostEventReview"
          value={askForPostEventReview}
          label={t('eventManagement:common.askForPostEventReview')}
          tooltip={
            <>
              <p>{t('eventManagement:common.postEventReviewTooltipLine1')}</p>
              <p>{t('eventManagement:common.postEventReviewTooltipLine2')}</p>
              <p>{t('eventManagement:common.postEventReviewTooltipLine3')}</p>
              <p>{t('eventManagement:common.postEventReviewTooltipLine4')}</p>
              <p>{t('eventManagement:common.postEventReviewTooltipLine5')}</p>
              <p>{t('eventManagement:common.postEventReviewTooltipLine6')}</p>
            </>
          }
          onToggle={handleAskForReviewChange}
        />
      </div>
      {askForPostEventReview && (
        <div className="event-form-review-card">
          <div className="event-form-input-row-flex-container">
            <CheckboxInput
              columnSize={12}
              name="positiveReview"
              label={t('eventManagement:common.requestPublicReviews')}
              value={positiveReview}
              setFieldValue={handlePositiveReviewChange}
              tooltip={
                <>
                  <p>{t('eventManagement:common.publicReviewsTooltipLine1')}</p>
                  <p>{t('eventManagement:common.publicReviewsTooltipLine2')}</p>
                </>
              }
            />
          </div>
          {positiveReview && (
            <React.Fragment>
              <div className="event-form-review-card__social-row">
                {hasSocialLinks && (
                  <SelectField
                    name="socialLinkService"
                    label={t('eventManagement:common.selectSocialLink')}
                    placeholder={t('eventManagement:common.selectSocialLink')}
                    options={socialLinksOptions}
                    value={socialLinkService}
                    onChange={(e) => setFieldValue('socialLinkService', e.target.value)}
                    additionalClass="event-form-review-card__select-wrap"
                  />
                )}
                <a
                  className="event-form-create-social-link"
                  href={SOCIAL_LINKS_PAGE}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PlusCircle size={16} />
                  {t('eventManagement:common.createSocialLink')}
                </a>
              </div>
              <div className="columns">
                <div className="column is-12 event-form-review-card__reply-row">
                  <div className="event-form-review-card__reply-label">
                    <span>{t('eventManagement:common.replyToEmail')}</span>
                    <Tooltip
                      name="replyToEmail"
                      tooltip={
                        <>
                          <p>{t('eventManagement:common.replyToEmailTooltipLine1')}</p>
                          <p>{t('eventManagement:common.replyToEmailTooltipLine2')}</p>
                          <p>{t('eventManagement:common.replyToEmailTooltipLine3')}</p>
                          <p>{t('eventManagement:common.replyToEmailTooltipLine4')}</p>
                        </>
                      }
                    />
                  </div>
                  <TextInput
                    columnSize={12}
                    name="replyTo"
                    label={t('eventManagement:common.replyToEmail')}
                    placeholder={t('eventManagement:common.replyToEmailPlaceholder')}
                    value={replyTo || ''}
                    setFieldValue={setFieldValue}
                    maxLength={255}
                  />
                  <div className="event-form-review-card__warning">
                    <Warning size={20} />
                    <div>{t('eventManagement:common.replyToEmailWarning')}</div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          )}
        </div>
      )}
    </React.Fragment>
  )
}

PostEventReviewSection.propTypes = {
  askForPostEventReview: PropTypes.bool,
  buildingId: PropTypes.number,
  positiveReview: PropTypes.bool,
  replyTo: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
  socialLinkService: PropTypes.string,
  socialLinksOptions: PropTypes.array,
}

export default PostEventReviewSection
